// Referee x Club — a W/D/L half-donut + For/Vs stats (Phase 3).
//
// React owns the wrapper, header, stats grid (reactive text) and the <svg>
// skeleton + empty `.fg-stats`; D3 owns the donut arcs, the svg sizing, and the
// `.fg-stats` W/D/L row (its top margin is geometry-derived, so it's written in
// the draw). The donut arc-tween is a port of the legacy `drawDonut` — the same
// shape as <HalfDonut> but with the widget's own `.rwt-*` DOM, so it can't reuse
// that component. Persistent skeleton (first draw snaps, later draws animate).
// Ref + team + season arrive from the bridge context (legacy shell selects).

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { arc as d3Arc, pie as d3Pie } from "d3-shape";
import { interpolate as d3Interpolate } from "d3-interpolate";
import { easeCubicInOut } from "d3-ease";
import { shapeRefWithTeams, type RefWithTeamsView } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

// arc/pie/interpolate typed with minimal local signatures (see HalfDonut).
interface ArcDatum {
  startAngle: number;
  endAngle: number;
  innerRadius?: number;
  outerRadius?: number;
}
interface ArcGen {
  (d: ArcDatum): string;
  innerRadius(r: number): ArcGen;
  outerRadius(r: number): ArcGen;
  cornerRadius(r: number): ArcGen;
}
interface Seg {
  value: number;
  color: string;
  key: string;
}
interface PieArc {
  data: Seg;
  startAngle: number;
  endAngle: number;
  value: number;
  index: number;
  padAngle: number;
}
interface PieGen {
  (data: Seg[]): PieArc[];
  sort(c: null): PieGen;
  value(fn: (d: Seg) => number): PieGen;
  padAngle(a: number): PieGen;
  startAngle(a: number): PieGen;
  endAngle(a: number): PieGen;
}
const arcFactory = d3Arc as unknown as () => ArcGen;
const pieFactory = d3Pie as unknown as () => PieGen;
const interpolate = d3Interpolate as <T>(a: T, b: T) => (t: number) => T;
type ArcPath = SVGPathElement & { _current?: PieArc };

const DURATION = 450;

function sizeSvg(svgEl: SVGSVGElement, body: HTMLElement): void {
  const sz = Math.max(
    68,
    Math.round(Math.min(body.clientWidth * 0.42, body.clientHeight * 0.7))
  );
  svgEl.setAttribute("width", String(sz));
  svgEl.setAttribute("height", String(sz));
}

function drawDonut(svgEl: SVGSVGElement, v: RefWithTeamsView, useTransition: boolean): void {
  const W = svgEl.clientWidth;
  const H = svgEl.clientHeight;
  if (W < 10 || H < 10) return;
  const cx = W / 2;
  const outerR = Math.min(cx - 2, H - 36);
  const cy = Math.round((H + outerR - 28) / 2);
  const innerR = outerR * 0.71;
  const halfD = arcFactory()({ startAngle: -Math.PI / 2, endAngle: Math.PI / 2, innerRadius: 0, outerRadius: outerR });
  const holeD = arcFactory()({ startAngle: -Math.PI / 2, endAngle: Math.PI / 2, innerRadius: 0, outerRadius: innerR });
  const svg = select(svgEl);
  const dur = useTransition ? DURATION : 0;
  const ease = easeCubicInOut;
  const pie = pieFactory().sort(null).value((d) => d.value).padAngle(0.08).startAngle(-Math.PI / 2).endAngle(Math.PI / 2);
  const arcGen = arcFactory().innerRadius(innerR).outerRadius(outerR).cornerRadius((outerR - innerR) * 0.35);
  const arcData = pie([
    { value: v.wPct, color: "#2ecc71", key: "W" },
    { value: v.dPct, color: "#888", key: "D" },
    { value: v.lPct, color: "#e74c3c", key: "L" },
  ]);

  svg.select(".rwt-bg").attr("transform", `translate(${cx},${cy})`).attr("d", halfD);

  let grad = svg.select<SVGRadialGradientElement>("#rwt-light");
  if (grad.empty()) {
    grad = svg.select("defs").append("radialGradient").attr("id", "rwt-light");
    grad.append("stop").attr("offset", "0%").attr("stop-color", "white").attr("stop-opacity", 0.18);
    grad.append("stop").attr("offset", "100%").attr("stop-color", "white").attr("stop-opacity", 0);
  }
  grad.attr("gradientUnits", "userSpaceOnUse")
    .attr("cx", cx - outerR * 0.24).attr("cy", cy - outerR * 0.56).attr("r", outerR * 1.1);

  let clip = svg.select<SVGClipPathElement>("#rwt-clip");
  if (clip.empty()) clip = svg.select("defs").append("clipPath").attr("id", "rwt-clip");
  clip.attr("transform", `translate(${cx},${cy})`);
  const cArcs = clip.selectAll<SVGPathElement, PieArc>("path").data(arcData, (d) => d.data.key);
  cArcs.enter().append("path").merge(cArcs).attr("d", (d) => arcGen(d));
  cArcs.exit().remove();

  const g = svg.select(".rwt-arcs").attr("transform", `translate(${cx},${cy})`);
  const paths = g.selectAll<SVGPathElement, PieArc>("path").data(arcData, (d) => d.data.key);
  const entering = paths.enter().append("path")
    .attr("class", "bento-fill")
    .attr("fill", (d) => d.data.color)
    .each(function (this: ArcPath, d) {
      this._current = { ...d, endAngle: d.startAngle };
    });
  entering.merge(paths)
    .attr("class", "bento-fill")
    .attr("fill", (d) => d.data.color)
    .transition().duration(dur).ease(ease)
    .attrTween("d", function (this: ArcPath, d) {
      const interp = interpolate(this._current as PieArc, d);
      return (t: number) => {
        this._current = interp(t);
        return arcGen(interp(t));
      };
    });
  paths.exit<PieArc>().transition().duration(dur).ease(ease)
    .attrTween("d", function (this: ArcPath) {
      const cur = this._current as PieArc;
      return (t: number) => arcGen(interpolate(cur, { ...cur, endAngle: cur.startAngle })(t));
    })
    .remove();

  svg.select(".rwt-hole").attr("transform", `translate(${cx},${cy})`).attr("d", holeD);
  svg.select(".rwt-lo").attr("transform", `translate(${cx},${cy})`).attr("d", halfD)
    .attr("fill", "url(#rwt-light)").attr("clip-path", "url(#rwt-clip)");

  const statsEl = svgEl.parentElement?.querySelector<HTMLElement>(".fg-stats");
  if (statsEl) {
    statsEl.innerHTML = `<span class="fg-w">${v.wPct}%</span><span class="fg-d">${v.dPct}%</span><span class="fg-l">${v.lPct}%</span>`;
    statsEl.style.marginTop = 8 - (H - cy) + "px";
  }
}

export interface RefereeXClubProps {
  fixture: string;
  ref?: string | null;
  team?: string | null;
  /** Season toggle: "This Season" | "Last Season" | "All Time". */
  refSeason?: string | null;
}

const PEN_ICON = (
  <svg width="10" height="14" viewBox="0 0 10 14" className="ref-pen-icon">
    <circle cx="5" cy="7" r="4.5" fill="none" stroke="#aaa" strokeWidth="1.2" />
    <circle cx="5" cy="7" r="1.8" fill="#aaa" />
  </svg>
);

export default function RefereeXClub({ fixture, ref, team, refSeason }: RefereeXClubProps) {
  const chosenRef = ref || "M. Oliver";
  const home = (fixture || "Arsenal v Everton").split(" v ")[0]?.trim() ?? "";
  const chosenTeam = team || home;
  // Data now from app-api (was the `getRefWithTeamsData` getter); the shaper + D3
  // donut are unchanged. Ref/team/season reshape client-side, no refetch.
  const { data, isPending, isError, refetch } = useWidgetData(
    "referee-x-club",
    useFixtureKey(fixture)
  );
  const v = shapeRefWithTeams(data ?? {}, chosenRef, chosenTeam, refSeason);

  // Hooks run unconditionally (above the early returns). `sig` is "" while
  // pending (v null) → the W/D/L signature once loaded, so the effect re-draws
  // when the fetch resolves and the svg has mounted.
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const firstRef = useRef(true);
  const sig = v ? `${v.wPct}|${v.dPct}|${v.lPct}` : "";

  useEffect(() => {
    const svgEl = svgRef.current;
    const wrap = wrapRef.current;
    if (!svgEl || !wrap || !v) return;
    const body = wrap.parentElement;
    const render = (useTransition: boolean) => {
      if (body) sizeSvg(svgEl, body);
      drawDonut(svgEl, v, useTransition);
    };
    render(!firstRef.current);
    firstRef.current = false;

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => render(false));
    if (body) ro.observe(body);
    return () => ro.disconnect();
    // `sig` captures the W/D/L percentages (the complete draw input); v read fresh.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!v) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="rwt-wrapper" ref={wrapRef}>
      <div className="stat-section-hdr rp-hdr-offset">{`${v.ref} – ${v.games} games`}</div>
      <div className="rwt-body">
        <div className="rwt-donut-col">
          <svg className="rwt-donut-svg" ref={svgRef}>
            <defs />
            <path className="rwt-bg" fill="none" />
            <g className="rwt-arcs" />
            <path className="rwt-hole" fill="none" />
            <path className="rwt-lo" pointerEvents="none" />
          </svg>
          <div className="fg-stats" />
        </div>
        <div className="rwt-stats-col">
          <div className="rwt-stats-grid">
            <span className="rwt-stat-lbl">For</span>
            <span className="rwt-stat-lbl">·</span>
            <span className="rwt-stat-lbl">Vs</span>
          </div>
          <div className="rwt-stats-grid">
            <span className="rwt-stat-val rwt-v-ycF">{v.ycF}</span>
            <div className="rwt-stat-center">
              <div className="card-yc card-icon-justify-end" />
            </div>
            <span className="rwt-stat-val rwt-v-ycA">{v.ycA}</span>
          </div>
          <div className="rwt-stats-grid">
            <span className="rwt-stat-val rwt-v-rcF">{v.rcF}</span>
            <div className="rwt-stat-center">
              <div className="card-rc card-icon-justify-end" />
            </div>
            <span className="rwt-stat-val rwt-v-rcA">{v.rcA}</span>
          </div>
          <div className="rwt-stats-grid">
            <span className="rwt-stat-val rwt-v-pF">{v.pF}</span>
            <div className="rwt-stat-center">{PEN_ICON}</div>
            <span className="rwt-stat-val rwt-v-pA">{v.pA}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
